import InstructionCard from '@/components/portal/InstructionCard';
import { leftPanelStyles } from '@/components/portal/LeftPanel/styles';
import { useNote } from '@/context/NoteProvider';
import { formatDate, formatTime } from '@/helpers/helper';
import {
  getSavedLastPage,
  getSavedSelectedCaseId,
  setSavedSelection,
} from '@/helpers/historySelection';
import { useAuth } from '@/hooks';
import { INoteHistory } from '@/interfaces/noteInterface';
import { brand } from '@/styles/shared';
import {
  Box,
  Pagination,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import HistoryClockIcon from '../icons/HistoryClockIcon';
import { previousHistoryTableStyles } from './styles';

interface PreviousHistoryTableProps {
  onNoteSelect?: (note: INoteHistory, isUserClick?: boolean) => void;
}

// Storage-backed persistence moved to helpers/historySelection

const PreviousHistoryTable = ({ onNoteSelect }: PreviousHistoryTableProps) => {
  const { token } = useAuth();
  const noteContext = useNote();
  const { getHistory, isSbConnect } = noteContext;
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [currentPage, setCurrentPage] = useState(getSavedLastPage());
  const [selectedNote, setSelectedNote] = useState<INoteHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const collapsible = !isDesktop; // collapse only on tablet/mobile
  const showHero = isDesktop || instructionsOpen;

  const historyData = noteContext.history;

  const scrollSelectedIntoView = useCallback((caseId: number) => {
    // Defer until DOM updates
    setTimeout(() => {
      const container = document.querySelector(
        '[data-testid="history-container"]'
      );
      const row = container?.querySelector(
        `[data-case-id="${caseId}"]`
      ) as HTMLElement | null;
      if (row && typeof row.scrollIntoView === 'function') {
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }, []);

  // Effect for initial load - fetch history for current page
  useEffect(() => {
    if (token) {
      setLoading(true);
      getHistory(currentPage).finally(() => setLoading(false));
    }
  }, [token, currentPage]); // getHistory is stable from NoteProvider

  // Fetch history function for page changes
  const fetchHistory = useCallback(
    async (page: number) => {
      if (!token) return;
      setLoading(true);
      await getHistory(page);
      setLoading(false);
    },
    [token]
  ); // noteContext.getHistory is stable from NoteProvider

  // Auto-select note when history loads for the current page
  useEffect(() => {
    // Skip if no data, already selected, or on mobile
    if (
      !historyData?.notes?.length ||
      hasAutoSelected ||
      selectedNote ||
      isMobileOrTablet
    )
      return;

    let noteToSelect: INoteHistory | undefined;
    const savedId = getSavedSelectedCaseId();
    const hasSavedSelection = savedId != null;

    // Prefer previously selected note if present in this page
    if (hasSavedSelection) {
      noteToSelect = historyData.notes.find(note => note.caseid === savedId);
    }

    // Fall back to first note only when there is no saved selection
    if (!noteToSelect && !hasSavedSelection) {
      noteToSelect = historyData.notes[0];
    }

    // If a saved selection exists but isn't on this page, still load it in the right panel
    if (!noteToSelect && hasSavedSelection && savedId != null) {
      setHasAutoSelected(true); // don't loop for this page
      noteContext.loadNote(savedId.toString());
      return;
    }

    if (noteToSelect) {
      setSelectedNote(noteToSelect);
      setHasAutoSelected(true);
      // Persist selection in case this was a restore after refresh
      setSavedSelection(noteToSelect.caseid, currentPage);
      noteContext.loadNote(noteToSelect.caseid.toString());
      if (onNoteSelect) {
        onNoteSelect(noteToSelect, false); // Auto-selection, not user click
      }
      scrollSelectedIntoView(noteToSelect.caseid);
    }
  }, [
    historyData,
    currentPage,
    hasAutoSelected,
    selectedNote,
    onNoteSelect,
    noteContext,
    scrollSelectedIntoView,
    isMobileOrTablet,
  ]); // noteContext.loadNote is stable

  // Call onNoteSelect when a note is selected
  useEffect(() => {
    if (selectedNote && onNoteSelect) {
      onNoteSelect(selectedNote);
    }
  }, [selectedNote, onNoteSelect]);

  // Keep list selection in sync with the note loaded in the right panel
  useEffect(() => {
    const currentId = noteContext.currentNoteId;
    if (!currentId || !historyData?.notes?.length) return;
    const match = historyData.notes.find(
      n => String(n.caseid) === String(currentId)
    );
    if (match) {
      setSelectedNote(match);
    }
  }, [noteContext.currentNoteId, historyData]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
    setHasAutoSelected(false); // Reset auto-selection for new page
    fetchHistory(value);
    // Scroll to top of history table
    const historyContainer = document.querySelector(
      '[data-testid="history-container"]'
    );
    if (historyContainer) {
      historyContainer.scrollTop = 0;
    }
  };

  const handleRowClick = (note: INoteHistory) => {
    setSelectedNote(note);
    // Persist selection and page
    setSavedSelection(note.caseid, currentPage);
    noteContext.loadNote(note.caseid.toString());
    if (onNoteSelect) {
      onNoteSelect(note, true); // User click
    }
    scrollSelectedIntoView(note.caseid);
  };

  if (!token) {
    return (
      <Box sx={previousHistoryTableStyles.loadingContainer}>
        <Typography sx={previousHistoryTableStyles.loadingText}>
          No authentication token found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={previousHistoryTableStyles.prevHistoryCointainer}>
      {/* Title and subtitle - only show on desktop or when instructions are open on mobile/tablet */}
      {showHero && (
        <Box sx={leftPanelStyles.titleSection}>
          <Typography variant="h1" sx={leftPanelStyles.title}>
            Create clinically formatted &<br />
            structured notes
          </Typography>
          <Typography variant="h2" sx={leftPanelStyles.subtitle}>
            with justified coding in real time as you speak.
          </Typography>
        </Box>
      )}

      {/* Instructions card - only show on mobile/tablet */}
      {collapsible && (
        <Box sx={{ marginBottom: '20px' }}>
          <InstructionCard
            title={
              collapsible ? 'Close instructions' : 'Viewing history is simple:'
            }
            collapsedTitle={
              collapsible ? 'Open instructions how to use' : undefined
            }
            contentTitle={
              collapsible ? 'Viewing history is simple:' : undefined
            }
            instructions={[
              {
                text: 'Tap on any case row to view the note details',
                type: 'green',
              },
              {
                text: 'Use pagination to browse through your note history',
                type: 'purple',
              },
            ]}
            collapsible={collapsible}
            defaultOpen={!collapsible}
            onOpenChange={collapsible ? setInstructionsOpen : undefined}
          />
        </Box>
      )}

      {/* History container */}
      <Box
        sx={previousHistoryTableStyles.historyContainer}
        data-testid="history-container"
      >
        {/* Header with icon and pagination */}
        <Box sx={previousHistoryTableStyles.headerContainer}>
          <Box sx={previousHistoryTableStyles.headerLeft}>
            <Box sx={previousHistoryTableStyles.iconContainer}>
              <HistoryClockIcon width={16} height={16} />
            </Box>
            <Typography sx={previousHistoryTableStyles.headerText}>
              Tap on case to open information
            </Typography>
          </Box>

          {/* Pagination in header */}
          {historyData && historyData.notes && historyData.notes.length > 0 && (
            <Pagination
              count={Math.ceil((historyData.number_of_jobs || 0) / 10)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={previousHistoryTableStyles.headerPagination}
              variant="outlined"
              aria-label="History pagination"
              data-current-page={currentPage}
              siblingCount={isMobileOrTablet ? 0 : 1}
              boundaryCount={isMobileOrTablet ? 1 : 1}
            />
          )}
        </Box>

        {/* Column headers */}
        <Box sx={previousHistoryTableStyles.columnHeadersContainer}>
          <Typography
            sx={[
              previousHistoryTableStyles.columnHeader,
              previousHistoryTableStyles.caseIdHeader,
            ]}
          >
            Case Id
          </Typography>
          <Typography
            sx={[
              previousHistoryTableStyles.columnHeader,
              previousHistoryTableStyles.patientIdHeader,
            ]}
          >
            Patient ID
          </Typography>
          <Typography
            sx={[
              previousHistoryTableStyles.columnHeader,
              previousHistoryTableStyles.dateHeader,
            ]}
          >
            Date
          </Typography>
          <Typography
            sx={[
              previousHistoryTableStyles.columnHeader,
              previousHistoryTableStyles.receivedHeader,
            ]}
          >
            Received
          </Typography>
          <Typography
            sx={[
              previousHistoryTableStyles.columnHeader,
              previousHistoryTableStyles.durationHeader,
            ]}
          >
            Duration
          </Typography>
        </Box>

        {/* History list */}
        <Box sx={previousHistoryTableStyles.historyList}>
          {loading ? (
            <Box sx={previousHistoryTableStyles.loadingRow}>
              <Typography sx={previousHistoryTableStyles.loadingText}>
                Loading history...
              </Typography>
            </Box>
          ) : historyData?.notes?.length ? (
            [...historyData.notes]
              .sort((a, b) => b.caseid - a.caseid)
              .map((note: INoteHistory) => {
                const isSelected = selectedNote?.caseid === note.caseid;
                const isNoteLoading = false; // Hook up if/when we track per-row loading
                const statusLower = (
                  (isSbConnect ? note.ecwStatus : note.cleanup_job_status) || ''
                ).toLowerCase();
                const isError = statusLower === 'failed';
                const isPending = statusLower === 'pending';
                const isRowCompleted = statusLower === 'completed';
                const isRightPanelCompletedForThisRow =
                  isSelected &&
                  noteContext.currentNoteId === String(note.caseid) &&
                  noteContext.status === 'completed';
                const showProcessing =
                  !isRightPanelCompletedForThisRow &&
                  (isNoteLoading || isPending);

                return (
                  <Box
                    key={note.caseid}
                    data-case-id={note.caseid}
                    role="button"
                    tabIndex={0}
                    aria-selected={isSelected}
                    onClick={() => handleRowClick(note)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(note);
                      }
                    }}
                    sx={[
                      previousHistoryTableStyles.historyRow,
                      isRowCompleted && previousHistoryTableStyles.completedRow,
                      isPending && previousHistoryTableStyles.pendingRow,
                      isError && previousHistoryTableStyles.errorRow,
                      isSelected && previousHistoryTableStyles.selectedRow,
                    ]}
                  >
                    <Typography sx={previousHistoryTableStyles.caseIdText}>
                      #{note.caseid}
                      {showProcessing && (
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            border: '2px solid #f3f3f3',
                            borderTop: `2px solid ${brand.purpleAccent}`,
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' },
                            },
                          }}
                        />
                      )}
                    </Typography>
                    <Typography
                      sx={[
                        previousHistoryTableStyles.rowText,
                        previousHistoryTableStyles.patientIdText,
                      ]}
                    >
                      {note.patientid || '-'}
                    </Typography>
                    <Typography
                      sx={[
                        previousHistoryTableStyles.rowText,
                        previousHistoryTableStyles.dateText,
                      ]}
                    >
                      {formatDate(note.created_on)}
                    </Typography>
                    <Typography
                      sx={[
                        previousHistoryTableStyles.rowText,
                        previousHistoryTableStyles.receivedText,
                      ]}
                    >
                      {formatTime(note.created_on)}
                    </Typography>
                    <Typography
                      sx={[
                        previousHistoryTableStyles.rowText,
                        previousHistoryTableStyles.durationText,
                      ]}
                    >
                      {showProcessing
                        ? 'Processing...'
                        : note.exam_time || '01:00'}
                    </Typography>
                  </Box>
                );
              })
          ) : (
            <Box sx={previousHistoryTableStyles.emptyStateRow}>
              <Typography sx={previousHistoryTableStyles.emptyStateText}>
                No previous records found.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pagination */}
        {historyData && historyData.total_pages > 1 && (
          <Box sx={previousHistoryTableStyles.paginationContainer}>
            <Pagination
              count={historyData.total_pages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={previousHistoryTableStyles.pagination}
              aria-label="History pagination"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PreviousHistoryTable;
